import React from 'react'
import { Box, Typography, Container } from '@mui/material'
import SharedButton from '../Button'
import { Helmet } from 'react-helmet-async'
import { styled } from '@mui/material/styles'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'

const MainContent = styled(Box)(
  ({ theme }) => `
    min-height: 100vh;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${theme.palette.background.default};
    padding: 24px;
`
)

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          <Helmet>
            <title>Error</title>
          </Helmet>
          <MainContent>
            <Container maxWidth="xs">
              <Box
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 36, color: '#ef4444' }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '22px', sm: '26px' },
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.3,
                  }}
                >
                  Something went wrong
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    maxWidth: 320,
                  }}
                >
                  An unexpected error occurred. Please try refreshing the page or go back to the homepage.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mt: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <SharedButton
                    label="Refresh Page"
                    startIcon={<RefreshOutlinedIcon sx={{ fontSize: 18 }} />}
                    onClick={() => window.location.reload()}
                  />
                  <SharedButton
                    label="Go to Homepage"
                    btnVariant="secondary"
                    startIcon={<HomeOutlinedIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      window.location.href = '/subscribers'
                    }}
                  />
                </Box>
              </Box>
            </Container>
          </MainContent>
        </>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
